import React from 'react';
import type { Blueprint, Kitchen } from '@rta/rta-blueprint-library';

import type { BlueprintPage } from '../types';
import { BlueprintPageFooter } from '../shared/blueprint-page-footer';
import { BlueprintPageHeader } from '../shared/blueprint-page-header';
import { BlueprintPageWrapper } from '../shared/blueprint-page-wrapper';
import { BlueprintPanelFooter } from '../shared/blueprint-panel-footer';
import { buildLandingZoneGroups, LANDING_ZONE_WIDTH, LandingZoneDiagram } from '../shared/landing-zone-diagram';

export { LANDING_ZONE_WIDTH };

export type BlueprintLandingZonePageProps = {
	page: BlueprintPage;
	blueprint: Blueprint;
	kitchen: Kitchen;
	customerName?: string;
	layoutDescription?: string;
	designerName?: string;
	creationDate?: string;
	threeDImageUrl?: string;
};

const DEFAULT_VENEER_THICKNESS = '.625';

const BODY_STYLE: React.CSSProperties = { flex: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', alignContent: 'center', columnGap: '14px', rowGap: '6px', padding: '4px 24px', minHeight: 0, overflow: 'hidden' };

/** Landing zones on scratch-coat kitchens (#340/#421), built to the finalized sheets' conventions. */
export const BlueprintLandingZonePage = (props: BlueprintLandingZonePageProps): React.ReactElement => {
	const { page, kitchen, customerName, layoutDescription, designerName, creationDate, threeDImageUrl } = props;

	// The same ordered group list the pages builder chunked; this sheet renders its own slice.
	const groups = buildLandingZoneGroups(kitchen);
	const [from, to] = page.landingZoneSlice ?? [0, groups.length];
	const sheetGroups = groups.slice(from, to);

	const veneer = kitchen.blueprintOverrides?.['landingZone.veneerThickness'] ?? DEFAULT_VENEER_THICKNESS;
	// The note is NOT a fixed string on real sheets: veneer thickness is the one invariant, and the
	// "All landing zones are X\"W" clause only when the WHOLE KITCHEN shares one flange — a sheet
	// that happens to hold one diagram must not claim "all" for a mixed kitchen.
	const flanges = [...new Set(groups.map(({ flangeW }) => flangeW))];
	const note = flanges.length === 1
		? `For MFG Reference: All landing zones are ${flanges[0]}"W | ${veneer}" veneer thickness`
		: `For MFG Reference: ${veneer}" veneer thickness`;

	return (
		<BlueprintPageWrapper pageNumber={page.pageNumber}>
			<BlueprintPageHeader
				title={page.title}
				pageNumber={page.pageNumber}
				customerName={customerName}
				layoutDescription={layoutDescription}
				designerName={designerName}
				creationDate={creationDate}
				threeDImageUrl={threeDImageUrl}
				showThumbnail={true}
			/>
			<div style={BODY_STYLE}>
				{sheetGroups.length === 0
					? <div style={{ fontSize: '14px', color: '#999' }}>No landing-zone appliances on the flagged island(s).</div>
					: sheetGroups.map((group) => <LandingZoneDiagram key={group.key} {...group} />)}
			</div>
			<div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
				<BlueprintPanelFooter scope={page.overrideScope ?? 'landing-zone'} notes={[note]} noteScope={page.overrideScope ?? 'landing-zone'} />
				<BlueprintPageFooter pageNumber={page.pageNumber} />
			</div>
		</BlueprintPageWrapper>
	);
};
